/**
 * The pure logic over the channel's curation.
 *
 * The person picks what they want Magnis to DO. Each choice implies module
 * packages and, for the ones that fetch data, a source to connect — they never
 * pick a "module" or a "source" by name.
 *
 * The MAPPINGS used to live here as four literals. They moved to the channel
 * (DEC-21): at five thousand packages a client-owned list can offer what the
 * channel does not carry, and nothing newly published could reach the first
 * screen without shipping a new application build. What stays is the part that
 * is logic rather than data — the dependency closure and the ordering — which
 * is pure, tested, and has no reason to travel over a network.
 */

/** One offer on the first screen, as the channel publishes it. */
export interface Capability {
  readonly id: string;
  readonly title: string;
  /** Modules this capability directly needs. */
  readonly modules: readonly string[];
  /** Source that feeds it, or null when it needs no account. */
  readonly source?: string | null;
  /** Whether it brings people into the graph (pulls in `contacts`). */
  readonly people?: boolean;
  /** Costs nothing and needs no account — safe to preselect. */
  readonly local?: boolean;
}

/**
 * The channel's `onboarding.json`, as the wizard reads it.
 *
 * `capabilities` is curation — a judgement no manifest states. The other
 * three are DERIVED by the builder from the manifests themselves
 * (`tier`, `requires_schemas`, `rpc_calls`), so that the hand-maintained
 * duplicate this file used to hold does not simply reappear elsewhere.
 */
export interface Curation {
  readonly capabilities: readonly Capability[];
  /** Installed always, never offered: `tier = "system"`. */
  readonly always: readonly string[];
  /** Module → the modules it hard-depends on. */
  readonly hard_deps: Readonly<Record<string, readonly string[]>>;
  /** Dependency-safe install order over every package the curation reaches. */
  readonly install_order: readonly string[];
}

/** Resolve the transitive hard-dependency closure of a module set. */
function closure(seed: readonly string[], hardDeps: Curation["hard_deps"]): Set<string> {
  const out = new Set<string>(seed);
  const queue = [...seed];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) break;
    for (const dep of hardDeps[next] ?? []) {
      if (!out.has(dep)) {
        out.add(dep);
        queue.push(dep);
      }
    }
  }
  return out;
}

export interface Resolution {
  /** Modules to install, in dependency-safe order. */
  readonly modules: readonly string[];
  /** Sources to offer, deduped — Email + Meetings is ONE Google connection. */
  readonly sources: readonly string[];
}

/** The empty offer: what a channel that recommends nothing resolves to. */
export const NO_CURATION: Curation = {
  capabilities: [],
  always: [],
  hard_deps: {},
  install_order: [],
};

/**
 * Turn picked capability ids into what actually has to happen.
 *
 * `contacts` is conditional: added exactly once when any people-bearing card is
 * picked, and not at all otherwise.
 *
 * A module the channel's order does not mention is APPENDED rather than
 * dropped: an install plan that silently omits a package the person picked
 * would install a different product from the one they chose.
 */
export function resolve(picked: readonly string[], curation: Curation): Resolution {
  const chosen = curation.capabilities.filter((c) => picked.includes(c.id));

  const seed = new Set<string>(curation.always);
  for (const c of chosen) {
    for (const m of c.modules) seed.add(m);
    if (c.people === true) seed.add("contacts");
  }

  const wanted = closure([...seed], curation.hard_deps);
  const ordered = curation.install_order.filter((m) => wanted.has(m));
  const rest = [...wanted].filter((m) => !ordered.includes(m)).sort();
  const modules = [...ordered, ...rest];

  const sources: string[] = [];
  for (const c of chosen) {
    const source = c.source;
    if (typeof source === "string" && source !== "" && !sources.includes(source)) {
      sources.push(source);
    }
  }

  return { modules, sources };
}

/** The capabilities preselected on first paint: everything that costs nothing. */
export function defaultPicks(curation: Curation): readonly string[] {
  return curation.capabilities.filter((c) => c.local === true).map((c) => c.id);
}

// ── The step sequence ────────────────────────────────────────────────────────

export type Step =
  | "welcome"
  | "modules"
  | "sources"
  | "agent"
  | "search"
  | "installing"
  | "done";

/**
 * The screens that carry "Step N of M". Welcome is an introduction and the
 * closing screens report rather than ask, so neither is numbered — announcing
 * "Step 1 of 4" on the welcome screen and again on the first real question
 * made two different screens claim to be the same step.
 */
export const NUMBERED_STEPS: readonly Step[] = ["modules", "sources", "agent", "search"];

const ALL_STEPS: readonly Step[] = [
  "welcome",
  "modules",
  "sources",
  "agent",
  "search",
  "installing",
  "done",
];

/**
 * The screens THIS run actually visits.
 *
 * `sources` only exists when something the person picked needs an account.
 * Deriving the numbering from a fixed list instead made the counter jump from
 * 1 straight to 3.
 */
export function stepSequence(hasSources: boolean): readonly Step[] {
  return ALL_STEPS.filter((s) => s !== "sources" || hasSources);
}

/** Position of `step` among the numbered screens of this run, or null. */
export function stepNumber(
  step: Step,
  hasSources: boolean,
): { readonly index: number; readonly total: number } | null {
  const numbered = NUMBERED_STEPS.filter((s) => s !== "sources" || hasSources);
  const index = numbered.indexOf(step);
  if (index === -1) return null;
  return { index: index + 1, total: numbered.length };
}

function neighbour(step: Step, hasSources: boolean, delta: 1 | -1): Step {
  const seq = stepSequence(hasSources);
  const at = seq.indexOf(step);
  if (at === -1) return "welcome";
  const target = seq[Math.min(seq.length - 1, Math.max(0, at + delta))];
  return target ?? "welcome";
}

export function nextStep(step: Step, hasSources: boolean): Step {
  return neighbour(step, hasSources, 1);
}

export function prevStep(step: Step, hasSources: boolean): Step {
  return neighbour(step, hasSources, -1);
}

/**
 * Where to pick up from a persisted `current_step`.
 *
 * Quitting mid-wizard used to restart at Welcome and re-run every install,
 * because the stored step was written but never read back. A value this run
 * does not visit — "sources" after the person unpicked everything needing an
 * account — resolves to a screen that exists rather than stranding them on a
 * blank one, and anything unrecognised starts over.
 */
export function resumeStep(
  stored: string | null | undefined,
  hasSources: boolean,
): Step {
  if (stored === null || stored === undefined) return "welcome";
  const seq = stepSequence(hasSources);
  if (seq.includes(stored as Step)) return stored as Step;
  // Known step, just not part of this run: fall to the screen before it.
  const atAll = ALL_STEPS.indexOf(stored as Step);
  if (atAll === -1) return "welcome";
  for (let i = atAll - 1; i >= 0; i--) {
    const candidate = ALL_STEPS[i];
    if (candidate !== undefined && seq.includes(candidate)) return candidate;
  }
  return "welcome";
}
